<template>
  <div class="tn-dashboard">
    <header class="tn-dash-head">
      <div>
        <h2 class="tn-dash-head__title">{{ greeting }}{{ firstName ? ', ' + firstName : '' }}</h2>
        <p class="tn-dash-head__sub">{{ today }} · Here is what's happening across Tanefer.</p>
      </div>
      <div class="tn-dash-head__actions">
        <v-btn outlined color="primary" :to="{ name: 'addHotel' }">
          <v-icon left size="20">mdi-bed-outline</v-icon> New hotel
        </v-btn>
        <v-btn depressed color="primary" :to="{ name: 'addPackage' }">
          <v-icon left size="20">mdi-plus</v-icon> New package
        </v-btn>
      </div>
    </header>

    <section class="tn-kpis" aria-label="Bookings overview">
      <router-link
        v-for="source in sources"
        :key="source.key"
        :to="{ name: source.route }"
        class="tn-kpi"
        :class="'tn-kpi--' + source.key"
      >
        <div class="tn-kpi__top">
          <span class="tn-kpi__icon"><v-icon size="22">{{ source.icon }}</v-icon></span>
          <span class="tn-kpi__label">{{ source.label }}</span>
        </div>
        <template v-if="state[source.key].loading">
          <v-skeleton-loader type="heading" class="mt-4" width="90"></v-skeleton-loader>
          <v-skeleton-loader type="text" class="mt-3" width="140"></v-skeleton-loader>
        </template>
        <template v-else-if="state[source.key].error">
          <div class="tn-kpi__value">—</div>
          <div class="tn-kpi__meta tn-kpi__meta--error">Couldn't load</div>
        </template>
        <template v-else>
          <div class="tn-kpi__value">{{ state[source.key].items.length.toLocaleString('en-US') }}</div>
          <div class="tn-kpi__meta">
            <span class="tn-kpi__delta" :class="{ 'tn-kpi__delta--zero': !recentCount(source.key) }">
              +{{ recentCount(source.key) }}
            </span>
            in the last 30 days
          </div>
        </template>
        <v-icon class="tn-kpi__arrow" size="18">mdi-arrow-right</v-icon>
      </router-link>
    </section>

    <div class="tn-dash-grid">
      <v-card class="tn-dash-card tn-dash-card--recent">
        <div class="tn-dash-card__head">
          <div>
            <h3 class="tn-dash-card__title">Recent bookings</h3>
            <p class="tn-dash-card__sub">The latest bookings across packages, adventures, cruises and hotels.</p>
          </div>
        </div>
        <v-skeleton-loader v-if="anyLoading" type="table-row-divider@6" class="px-4 pb-4"></v-skeleton-loader>
        <div v-else-if="$vuetify.breakpoint.xsOnly" class="tn-recent-list">
          <router-link
            v-for="item in recentBookings"
            :key="item.uid"
            :to="{ name: item.detail, params: { id: item.id } }"
            class="tn-recent-list__item"
          >
            <div class="tn-recent-list__row">
              <span class="tn-booking-id">#{{ item.id }}</span>
              <span class="tn-type" :class="'tn-type--' + item.type"><v-icon size="14">{{ item.icon }}</v-icon> {{ item.typeLabel }}</span>
              <span class="tn-recent-list__amount">{{ money(item.total_price) }}</span>
            </div>
            <div class="tn-recent-list__row tn-recent-list__row--meta">
              <v-chip x-small label class="tn-status-chip" :class="'tn-status-chip--' + statusColor(item.status)">{{ statusLabel(item.status) }}</v-chip>
              <span>{{ travellers(item) }} · {{ date(item.created_at) }}</span>
            </div>
          </router-link>
          <div v-if="!recentBookings.length" class="tn-empty">
            <v-icon size="36">mdi-inbox-outline</v-icon>
            <div>No bookings yet</div>
          </div>
        </div>
        <v-data-table
          v-else
          :headers="recentHeaders"
          :items="recentBookings"
          item-key="uid"
          hide-default-footer
          disable-sort
          class="tn-recent-table"
          @click:row="openBooking"
        >
          <template v-slot:item.id="{ item }">
            <span class="tn-booking-id">#{{ item.id }}</span>
            <div class="tn-booking-title">{{ item.title }}</div>
          </template>
          <template v-slot:item.typeLabel="{ item }">
            <span class="tn-type" :class="'tn-type--' + item.type">
              <v-icon size="14">{{ item.icon }}</v-icon> {{ item.typeLabel }}
            </span>
          </template>
          <template v-slot:item.total_price="{ item }">
            <span class="font-weight-medium">{{ money(item.total_price) }}</span>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip small label class="tn-status-chip" :class="'tn-status-chip--' + statusColor(item.status)">
              {{ statusLabel(item.status) }}
            </v-chip>
          </template>
          <template v-slot:item.created_at="{ item }">
            <span class="text-no-wrap">{{ date(item.created_at) }}</span>
          </template>
          <template v-slot:no-data>
            <div class="tn-empty">
              <v-icon size="36">mdi-inbox-outline</v-icon>
              <div>No bookings yet</div>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <div class="tn-dash-side">
        <v-card class="tn-dash-card">
          <div class="tn-dash-card__head">
            <h3 class="tn-dash-card__title">Needs attention</h3>
          </div>
          <div class="tn-attention">
            <router-link :to="{ name: 'notifications' }" class="tn-attention__item">
              <span class="tn-attention__icon tn-attention__icon--orange"><v-icon size="20">mdi-bell-ring-outline</v-icon></span>
              <span class="tn-attention__text">
                <strong>{{ unreadCount }}</strong> unread notification{{ unreadCount === 1 ? '' : 's' }}
              </span>
              <v-icon size="18" class="tn-attention__chevron">mdi-chevron-right</v-icon>
            </router-link>
            <router-link :to="{ name: 'hotelRefunds' }" class="tn-attention__item">
              <span class="tn-attention__icon tn-attention__icon--red"><v-icon size="20">mdi-cash-refund</v-icon></span>
              <span class="tn-attention__text">
                <template v-if="refunds.loading">Checking refunds…</template>
                <template v-else-if="refunds.error">Couldn't load refunds</template>
                <template v-else><strong>{{ refunds.pending }}</strong> refund{{ refunds.pending === 1 ? '' : 's' }} to transfer</template>
              </span>
              <v-icon size="18" class="tn-attention__chevron">mdi-chevron-right</v-icon>
            </router-link>
            <div class="tn-attention__item tn-attention__item--static">
              <span class="tn-attention__icon tn-attention__icon--amber"><v-icon size="20">mdi-timer-sand</v-icon></span>
              <span class="tn-attention__text">
                <template v-if="anyLoading">Checking payments…</template>
                <template v-else><strong>{{ awaitingPayment }}</strong> booking{{ awaitingPayment === 1 ? '' : 's' }} awaiting payment</template>
              </span>
            </div>
          </div>
        </v-card>

        <v-card class="tn-dash-card">
          <div class="tn-dash-card__head">
            <h3 class="tn-dash-card__title">Quick actions</h3>
          </div>
          <div class="tn-quick">
            <router-link v-for="action in quickActions" :key="action.label" :to="action.to" class="tn-quick__item">
              <v-icon size="22">{{ action.icon }}</v-icon>
              <span>{{ action.label }}</span>
            </router-link>
          </div>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import { packageBookings, activitiesBookings, cruisesBookings, hotelsBookings, adminHotelRefunds, headers } from '../links'
import { formatDate, formatMoney, statusColor, statusLabel } from '../utils/format'

const DAY = 24 * 60 * 60 * 1000

const sources = [
  { key: 'packages', label: 'Package bookings', short: 'Package', icon: 'mdi-ticket-confirmation-outline', url: packageBookings, route: 'tripBookings', detail: 'packageBooking' },
  { key: 'adventures', label: 'Adventure bookings', short: 'Adventure', icon: 'mdi-compass-outline', url: activitiesBookings, route: 'adventuresBookings', detail: 'activityBooking' },
  { key: 'cruises', label: 'Cruise bookings', short: 'Cruise', icon: 'mdi-sail-boat', url: cruisesBookings, route: 'cruisesBookings', detail: 'cruiseBooking' },
  { key: 'hotels', label: 'Hotel bookings', short: 'Hotel', icon: 'mdi-office-building-outline', url: hotelsBookings, route: 'hotelsBookings', detail: 'hotelBooking' }
]

function toTime (value) {
  const time = new Date(typeof value === 'string' ? value.replace(' ', 'T') : value).getTime()
  return isNaN(time) ? 0 : time
}

export default {
  name: 'Home',
  data () {
    const state = {}
    sources.forEach(source => { state[source.key] = { loading: true, error: false, items: [] } })
    return {
      sources,
      state,
      refunds: { loading: true, error: false, pending: 0 },
      recentHeaders: [
        { text: 'Booking', value: 'id' },
        { text: 'Type', value: 'typeLabel' },
        { text: 'Amount', value: 'total_price', align: 'end' },
        { text: 'Status', value: 'status' },
        { text: 'Created', value: 'created_at' }
      ],
      quickActions: [
        { label: 'New package', icon: 'mdi-beach', to: { name: 'addPackage' } },
        { label: 'New hotel', icon: 'mdi-bed-outline', to: { name: 'addHotel' } },
        { label: 'New cruise', icon: 'mdi-ferry', to: { name: 'addCruise' } },
        { label: 'Trip cities', icon: 'mdi-city-variant-outline', to: { name: 'tripsCities' } },
        { label: 'Hotel seasons', icon: 'mdi-calendar-range', to: { name: 'seasons' } },
        { label: 'Fee settings', icon: 'mdi-cash-multiple', to: { name: 'settingFees' } }
      ]
    }
  },
  computed: {
    firstName () {
      const user = this.$store.state.user
      return user && user.name ? user.name.split(' ')[0] : ''
    },
    greeting () {
      const hour = new Date().getHours()
      if (hour < 12) return 'Good morning'
      if (hour < 18) return 'Good afternoon'
      return 'Good evening'
    },
    today () {
      return new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    },
    unreadCount () {
      return this.$store.state.unreadCount
    },
    anyLoading () {
      return sources.some(source => this.state[source.key].loading)
    },
    allBookings () {
      return sources.reduce((all, source) => all.concat(this.state[source.key].items.map(item => ({
        ...item,
        uid: source.key + '-' + item.id,
        type: source.key,
        typeLabel: source.short,
        icon: source.icon,
        detail: source.detail,
        title: item.package && item.package.title ? item.package.title : ''
      }))), [])
    },
    recentBookings () {
      return this.allBookings
        .slice()
        .sort((a, b) => toTime(b.created_at) - toTime(a.created_at))
        .slice(0, 8)
    },
    awaitingPayment () {
      return this.allBookings.filter(item => statusColor(item.status) === 'warning').length
    }
  },
  methods: {
    statusColor,
    statusLabel,
    date (value) {
      return formatDate(value)
    },
    money (value) {
      return formatMoney(value)
    },
    travellers (item) {
      const adults = Number(item.adults) || 0
      const children = Number(item.children) || 0
      const parts = [`${adults} adult${adults === 1 ? '' : 's'}`]
      if (children) parts.push(`${children} child${children === 1 ? '' : 'ren'}`)
      return parts.join(', ')
    },
    recentCount (key) {
      const since = Date.now() - 30 * DAY
      return this.state[key].items.filter(item => toTime(item.created_at) >= since).length
    },
    openBooking (item) {
      this.$router.push({ name: item.detail, params: { id: item.id } })
    },
    load () {
      const auth = { headers: headers(this.$cookies.get('userToken')) }
      sources.forEach(source => {
        const slot = this.state[source.key]
        this.$http.get(source.url, auth).then(response => {
          slot.items = Array.isArray(response.body) ? response.body : []
        }).catch(() => {
          slot.error = true
        }).finally(() => {
          slot.loading = false
        })
      })
      this.$http.get(adminHotelRefunds, auth).then(response => {
        const refunds = response.body.refunds || []
        this.refunds.pending = refunds.filter(refund => !refund.is_transferred).length
      }).catch(() => {
        this.refunds.error = true
      }).finally(() => {
        this.refunds.loading = false
      })
    }
  },
  created () {
    this.load()
  }
}
</script>

<style lang="scss">
.tn-dash-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.tn-dash-head__title {
  margin: 0;
  color: var(--tn-text);
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -.02em;
}

.tn-dash-head__sub {
  margin: 4px 0 0 !important;
  color: var(--tn-muted);
  font-size: 14.5px;
}

.tn-dash-head__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tn-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 1263px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 599px) {
    gap: 12px;
  }
}

.tn-kpi {
  --kpi: var(--v-primary-base);
  position: relative;
  display: block;
  padding: 20px;
  border: 1px solid var(--tn-border);
  border-radius: var(--tn-radius);
  background: var(--tn-surface);
  box-shadow: var(--tn-shadow-sm);
  color: inherit !important;
  text-decoration: none;
  transition: box-shadow .18s, transform .18s, border-color .18s;

  &:hover,
  &:focus-visible {
    border-color: #D3DAE6;
    box-shadow: var(--tn-shadow-md);
    transform: translateY(-1px);
    outline: none;

    .tn-kpi__arrow {
      opacity: 1;
      transform: translateX(0);
    }
  }

  &--adventures { --kpi: #DF731C; }
  &--cruises { --kpi: #0E9384; }
  &--hotels { --kpi: #6941C6; }
}

.tn-kpi__top {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tn-kpi__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--kpi) 12%, transparent);

  .v-icon {
    color: var(--kpi) !important;
  }
}

.tn-kpi__label {
  color: var(--tn-muted);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}

@media (max-width: 599px) {
  .tn-kpi {
    padding: 14px;
  }

  .tn-kpi__top {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .tn-kpi__value {
    margin-top: 10px;
    font-size: 26px;
  }

  .tn-kpi__meta {
    font-size: 12px;
  }

  .tn-kpi__arrow {
    display: none !important;
  }
}

.tn-kpi__value {
  margin-top: 16px;
  color: var(--tn-text);
  font-size: 32px;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -.02em;
}

.tn-kpi__meta {
  margin-top: 8px;
  color: var(--tn-muted);
  font-size: 13px;

  &--error {
    color: var(--v-error-base);
  }
}

.tn-kpi__delta {
  margin-right: 2px;
  padding: 1px 6px;
  border-radius: 6px;
  background: #E7F6EC;
  color: #15803D;
  font-weight: 600;

  &--zero {
    background: #F1F5F9;
    color: var(--tn-muted);
  }
}

.tn-kpi__arrow {
  position: absolute !important;
  top: 22px;
  right: 18px;
  color: var(--tn-muted) !important;
  opacity: 0;
  transform: translateX(-4px);
  transition: opacity .18s, transform .18s;
}

.tn-dash-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 24px;
  align-items: start;

  @media (max-width: 1263px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.tn-dash-side {
  display: grid;
  gap: 24px;
}

.tn-dash-card.v-card {
  overflow: hidden;
}

.tn-dash-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 20px 14px;
}

.tn-dash-card__title {
  margin: 0;
  color: var(--tn-text);
  font-size: 16px;
  font-weight: 700;
}

.tn-dash-card__sub {
  margin: 2px 0 0 !important;
  color: var(--tn-muted);
  font-size: 13px;
}

.tn-recent-table tbody tr {
  cursor: pointer;
}

.tn-booking-id {
  color: var(--v-primary-base);
  font-weight: 600;
}

.tn-booking-title {
  max-width: 220px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--tn-muted);
  font-size: 12.5px;
}

.tn-type {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 6px;
  background: #EEF2FF;
  color: #3538CD;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;

  .v-icon {
    color: inherit !important;
  }

  &--packages { background: #EAF1FC; color: #1F5FBF; }
  &--adventures { background: #FDF0E6; color: #B35A12; }
  &--cruises { background: #E6F6F4; color: #0B7A6E; }
  &--hotels { background: #F1ECFB; color: #5B34B4; }
}

.tn-recent-list__item {
  display: block;
  padding: 12px 16px;
  border-top: 1px solid #EEF1F5;
  color: var(--tn-text) !important;
  text-decoration: none;
}

.tn-recent-list__row {
  display: flex;
  align-items: center;
  gap: 8px;

  &--meta {
    margin-top: 6px;
    color: var(--tn-muted);
    font-size: 12.5px;
  }
}

.tn-recent-list__amount {
  margin-left: auto;
  font-weight: 600;
}

.tn-attention {
  padding: 0 8px 10px;
}

.tn-attention__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  color: var(--tn-text-2) !important;
  font-size: 14px;
  text-decoration: none;
  transition: background .15s;

  strong {
    color: var(--tn-text);
  }

  &:not(&--static):hover {
    background: var(--tn-subtle);
  }
}

.tn-attention__text {
  flex: 1 1 auto;
}

.tn-attention__chevron {
  color: #A0AEC0 !important;
}

.tn-attention__icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;

  &--orange { background: #FDF0E6; .v-icon { color: #C2620F !important; } }
  &--red { background: #FDECEC; .v-icon { color: #C81E1E !important; } }
  &--amber { background: #FEF6E4; .v-icon { color: #B26B00 !important; } }
}

.tn-quick {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  padding: 0 16px 16px;
}

.tn-quick__item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 14px;
  border: 1px solid var(--tn-border);
  border-radius: 10px;
  color: var(--tn-text-2) !important;
  font-size: 13.5px;
  font-weight: 600;
  text-decoration: none;
  transition: border-color .15s, background .15s;

  .v-icon {
    color: var(--v-primary-base) !important;
  }

  &:hover {
    border-color: #C9D6EE;
    background: #F6F9FE;
  }
}
</style>
